import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { api, ApiError } from "../api";
import { useCan, useSession } from "../auth/session";
import { Card, EmptyState, Field, InlineAlert, MoreMenu, PageHeader, PageShell, StatusChip, icons, useToast } from "../company-ui";
import { Tip } from "../help/Tip";
import { useCompanyScope } from "../shell/company-scope";
import "./programs.css";

/**
 * Programs (ALN-34, docs/pages/programs.md), laid out as the mockup's v25 programs view: the four
 * tiles, the sortable programs table with each wine's harvest-to-release timeline, and the inline
 * New program form. Plan figures are estimates; Inventory at cost is posted book value.
 */
type Stage = "sourcing" | "farming" | "receiving" | "crush_press" | "fermentation" | "aging" | "blending" | "bottling" | "warehouse";
interface Variance { state: "none" | "ok" | "warn" | "bad"; direction: "over" | "under" | null; deltaCents: number; pct: number | null; stagesOutside: number }
export interface ProgramRow {
  id: string; name: string; vintage: number; wineType: string; status: string; companyId: string; companyName: string;
  route: "none" | "stainless" | "barrel"; agingMonths: number; fruitSource: string; targetCases: number;
  timeline: { phases: { stage: Stage; start: number; end: number; months: number }[]; nowMonth: number; label: string };
  projectedCases: number; planCostPerCaseCents: number | null; planSource: "working" | "approved" | "replan"; currentStage: Stage | null;
  variance: Variance; notCosted: string[]; notCostedNames: string[]; canCancel: boolean;
  cancelled: { at: string; by: string; reason: string } | null;
}
interface Tiles {
  programs: number; targetCases: number; inventoryCents: number; inventoryThrough: string | null; stagesOutsidePrograms: number;
  materialityCents: number | null; close: { period: string; status: string; canOpen: boolean };
}
interface NewForm {
  companies: { id: string; name: string }[]; showCompany: boolean; defaultCompanyId: string | null;
  templates: { id: string; name: string; fruitSource: string | null; baseTons: number | null; baseCases: number | null; tonsPerAcre: number | null }[];
  estate: boolean; tonsPerAcre: number | null; unmatchedPools: { name: string; kind: string; options: { id: string; name: string }[] }[];
}

const SUBTITLE = "Every wine in production: what it should cost per case, and how spending so far compares with the plan.";
const WINE: Record<string, string> = { red: "Red", white: "White", rose: "Rosé", sparkling: "Sparkling", dessert: "Dessert", other: "Other" };
const SOURCE: Record<string, string> = { purchased: "Purchased", estate: "Estate", mixed: "Mixed", bulk_wine: "Bulk wine" };
const CLOSE_STATUS: Record<string, string> = { draft: "Draft", importing: "Importing", mapped: "Mapped", allocated: "Allocated", reviewed: "Reviewed", posting: "Posting", posted: "Posted", reopened: "Reopened", failed: "Failed", adjusting: "Adjusting", submitted: "Submitted", approved: "Approved", reversed: "Reversed" };
const usd = (cents: number, digits = 2) => (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits });
const monthName = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const day = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const num = (n: number, max = 1) => n.toLocaleString("en-US", { maximumFractionDigits: max });
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Check your connection and try again.");
const routeLabel = (p: ProgramRow) => (p.route === "none" ? "None" : `${p.route === "barrel" ? "Barrel" : "Stainless"}, ${num(p.agingMonths)} mo`);

/* ---------------------------------------------------------------- sorting */

type SortKey = "name" | "route" | "source" | "timeline" | "projected" | "cost" | "variance";
const COLUMNS: { key: SortKey; label: string; tip?: string; right?: boolean }[] = [
  { key: "name", label: "Program" }, { key: "route", label: "Route", tip: "Route" }, { key: "source", label: "Source" },
  { key: "timeline", label: "Harvest to release", tip: "Harvest to release" }, { key: "projected", label: "Projected cases", tip: "Projected cases", right: true },
  { key: "cost", label: "Plan cost / case", tip: "Plan cost / case", right: true }, { key: "variance", label: "Actual vs plan", tip: "Actual vs plan" },
];
interface Sort { key: SortKey; dir: "asc" | "desc" }
const RANK = { none: 0, ok: 1, warn: 2, bad: 3 };
function compare(a: ProgramRow, b: ProgramRow, key: SortKey): number | null {
  switch (key) {
    case "name": return a.name.localeCompare(b.name);
    case "route": return routeLabel(a).localeCompare(routeLabel(b));
    case "source": return a.fruitSource.localeCompare(b.fruitSource);
    case "timeline": return (a.timeline.phases[0]?.start ?? 0) - (b.timeline.phases[0]?.start ?? 0);
    case "projected": return a.projectedCases - b.projectedCases;
    case "cost": return a.planCostPerCaseCents === null || b.planCostPerCaseCents === null ? null : a.planCostPerCaseCents - b.planCostPerCaseCents;
    case "variance": return RANK[a.variance.state] - RANK[b.variance.state] || a.variance.deltaCents - b.variance.deltaCents;
  }
}
/** Sorted rows. Not costed yet rows have no plan cost, so they sort last both ways (TC-PROG-096). */
function sortRows(rows: ProgramRow[], s: Sort): ProgramRow[] {
  const sign = s.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (s.key === "cost" && (a.planCostPerCaseCents === null) !== (b.planCostPerCaseCents === null)) return a.planCostPerCaseCents === null ? 1 : -1;
    return (compare(a, b, s.key) ?? 0) * sign || a.name.localeCompare(b.name);
  });
}
/** The sort is remembered per person on this device (TC-PROG-002). */
function useRememberedSort(userId: string | undefined): [Sort, (s: Sort) => void] {
  const storageKey = `avero.programs.sort.${userId ?? "anon"}`;
  const read = (): Sort => {
    try {
      const v = JSON.parse(localStorage.getItem(storageKey) ?? "null") as Sort | null;
      if (v && COLUMNS.some(c => c.key === v.key) && (v.dir === "asc" || v.dir === "desc")) return v;
    } catch { /* storage blocked: default sort */ }
    return { key: "name", dir: "asc" };
  };
  const [sort, setSort] = useState<Sort>(read);
  useEffect(() => { setSort(read()); }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps
  return [sort, s => { setSort(s); try { localStorage.setItem(storageKey, JSON.stringify(s)); } catch { /* not remembered */ } }];
}

/* ---------------------------------------------------------------- page */

export function ProgramsPage() {
  const canSee = useCan("costs.view");
  const canPlan = useCan("programs.plan");
  const session = useSession();
  const { companyId } = useCompanyScope();
  const search = new URLSearchParams(useSearch());
  const [, navigate] = useLocation();
  const [status, setStatus] = useState<"active" | "cancelled">("active");
  const [creating, setCreating] = useState(search.get("new") === "1");
  const [cancelling, setCancelling] = useState<ProgramRow | null>(null);
  const [sort, setSort] = useRememberedSort(session.data?.user?.id);
  const newButton = useRef<HTMLButtonElement>(null);
  const q = new URLSearchParams({ status, ...(companyId ? { companyId } : {}) }).toString();
  const list = useQuery({ queryKey: ["programs", status, companyId], queryFn: () => api<{ programs: ProgramRow[]; tiles: Tiles }>(`/programs?${q}`), enabled: canSee });
  const rows = useMemo(() => sortRows(list.data?.programs ?? [], sort), [list.data, sort]);

  if (!canSee) {
    return (
      <PageShell>
        <PageHeader title="Programs" subtitle={SUBTITLE} />
        <InlineAlert variant="info" title="You don't have access to programs. Ask an owner or controller." />
      </PageShell>
    );
  }

  const closeForm = () => {
    setCreating(false);
    if (search.get("new")) navigate("/programs", { replace: true });
    requestAnimationFrame(() => newButton.current?.focus());
  };
  return (
    <PageShell>
      <PageHeader title="Programs" subtitle={SUBTITLE} actions={canPlan && (
        <button ref={newButton} type="button" className="btn btn--primary" aria-expanded={creating} onClick={() => (creating ? closeForm() : setCreating(true))}>{icons.plus}New program</button>
      )} />
      <div className="stack">
        {creating && canPlan && <NewProgramForm initialTemplate={search.get("template")} onClose={closeForm} />}
        <ProgramTiles tiles={list.data?.tiles ?? null} loading={list.isPending} />
        {cancelling && <CancelProgram program={cancelling} onDone={() => setCancelling(null)} />}
        <Card title="Programs" headingId="programs-h" description={status === "cancelled" ? "Cancelled programs, read only." : "Select a program to plan it stage by stage. Select a column heading to sort."}
          actions={
            <div className="progtools">
              <label className="progtools__f">
                <span>Show</span>
                <select value={status} onChange={e => setStatus(e.target.value as "active" | "cancelled")}>
                  <option value="active">Active</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </label>
              <label className="progtools__f progtools__sort">
                <span>Sort by</span>
                <select value={`${sort.key}:${sort.dir}`} onChange={e => { const [key, dir] = e.target.value.split(":"); setSort({ key: key as SortKey, dir: dir as Sort["dir"] }); }}>
                  {COLUMNS.flatMap(c => [
                    <option key={`${c.key}:asc`} value={`${c.key}:asc`}>{c.label}, {c.key === "name" || c.key === "route" || c.key === "source" ? "A to Z" : "low to high"}</option>,
                    <option key={`${c.key}:desc`} value={`${c.key}:desc`}>{c.label}, {c.key === "name" || c.key === "route" || c.key === "source" ? "Z to A" : "high to low"}</option>,
                  ])}
                </select>
              </label>
            </div>
          }>
          {list.isPending ? <TableSkeleton />
            : list.isError ? (
              <EmptyState icon={icons.warning} title="Programs couldn't load. Try again in a minute; nothing was changed."
                action={<button type="button" className="btn" onClick={() => void list.refetch()}>Retry</button>} />
            ) : rows.length === 0 ? (
              status === "cancelled" ? <EmptyState title="No cancelled programs." />
                : <EmptyState title="No programs yet." description="A program is one wine you plan to make, from fruit to release. Start one from a template with New program."
                    action={<Link className="btn" href="/templates">See templates</Link>} />
            ) : (
              <ProgramsTable rows={rows} sort={sort} onSort={setSort} onCancel={setCancelling} />
            )}
        </Card>
      </div>
    </PageShell>
  );
}

/* ---------------------------------------------------------------- tiles */

function ProgramTiles({ tiles, loading }: { tiles: Tiles | null; loading: boolean }) {
  if (loading || !tiles) {
    return (
      <div className="tiles" aria-busy="true" aria-label="Loading totals">
        {["Programs in production", "Inventory at cost", "Programs with a stage outside tolerance", "Monthly close"].map(k => (
          <div key={k} className="card tile progskel"><div className="tile__k">{k}</div><i /><i /></div>
        ))}
      </div>
    );
  }
  const floor = tiles.materialityCents === null ? "the materiality floor" : usd(tiles.materialityCents, 0);
  return (
    <div className="tiles">
      <div className="card tile">
        <div className="tile__k">Programs in production</div>
        <div className="tile__v">{num(tiles.programs, 0)}</div>
        <div className="tile__s muted">{num(tiles.targetCases, 0)} target cases</div>
      </div>
      <div className="card tile">
        <div className="tile__k">Inventory at cost <Tip term="Inventory at cost" /></div>
        <div className="tile__v">{usd(tiles.inventoryCents, tiles.inventoryCents % 100 ? 2 : 0)}</div>
        <div className="tile__s muted">{tiles.inventoryThrough ? `All pools, as of ${monthName(tiles.inventoryThrough)}` : "No month closed yet"}</div>
      </div>
      <div className="card tile">
        <div className="tile__k">Programs with a stage outside tolerance <Tip term="Programs with a stage outside tolerance" /></div>
        <div className={`tile__v${tiles.stagesOutsidePrograms ? " progwarn" : ""}`}>{num(tiles.stagesOutsidePrograms, 0)}</div>
        <div className="tile__s muted">Over or under plan by less than {floor} is ignored</div>
      </div>
      <div className="card tile">
        <div className="tile__k">Monthly close</div>
        <div className="tile__v">{CLOSE_STATUS[tiles.close.status] ?? tiles.close.status}</div>
        <div className="tile__s">
          {tiles.close.canOpen
            ? <Link href="/absorption">{monthName(tiles.close.period)} close</Link>
            : <span className="muted">{monthName(tiles.close.period)}: {CLOSE_STATUS[tiles.close.status] ?? tiles.close.status}</span>}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- table */

function ProgramsTable({ rows, sort, onSort, onCancel }: { rows: ProgramRow[]; sort: Sort; onSort: (s: Sort) => void; onCancel: (p: ProgramRow) => void }) {
  return (
    <div className="tablewrap progtable" role="region" aria-labelledby="programs-h" tabIndex={0}>
      <table className="t">
        <thead>
          <tr>
            {COLUMNS.map(c => {
              const active = sort.key === c.key;
              return (
                <th key={c.key} scope="col" className={c.right ? "r" : undefined} aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                  <span className="progth">
                    <button type="button" className="progsort" onClick={() => onSort({ key: c.key, dir: active && sort.dir === "asc" ? "desc" : "asc" })}>
                      {c.label}<span className="progsort__i" aria-hidden="true">{active ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}</span>
                    </button>
                    {c.tip && <Tip term={c.tip} />}
                  </span>
                </th>
              );
            })}
            <th scope="col"><span className="sr">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.id}>
              <th scope="row" className="progname">
                {p.status === "cancelled"
                  ? <span className="progname__n">{p.name}</span>
                  : <Link className="progname__n" href={`/builder?program=${p.id}${p.currentStage ? `&stage=${p.currentStage}` : ""}`}>{p.name}</Link>}
                <span className="progname__m">{WINE[p.wineType] ?? p.wineType} · {p.companyName}</span>
                {p.cancelled && <span className="progname__c small muted">Cancelled {day(p.cancelled.at)} by {p.cancelled.by}</span>}
              </th>
              <td className="progroute">{routeLabel(p)}</td>
              <td>{SOURCE[p.fruitSource] ?? p.fruitSource}</td>
              <td><Timeline t={p.timeline} /></td>
              <td className="r num">{num(p.projectedCases)}</td>
              <td className="r num">
                {p.planCostPerCaseCents === null
                  ? p.notCostedNames.length ? <span className="muted">Not costed yet <NotCostedTip names={p.notCostedNames} /></span> : <span className="muted">—</span>
                  : usd(p.planCostPerCaseCents)}
              </td>
              <td><VarianceCell v={p.variance} /></td>
              <td className="r">{p.canCancel && <MoreMenu className="progmore" name={p.name} items={[{ label: "Cancel program", onSelect: () => onCancel(p) }]} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The plan cost cell's tip names the pool to set up (programs B, Not costed yet). */
function NotCostedTip({ names }: { names: string[] }) {
  const text = names.length === 1 ? `Not costed yet. Set a rate for ${names[0]} in Rates and pools.` : "Not costed yet. Set rates in Rates and pools.";
  return <span className="progtipnote" tabIndex={0} role="note" aria-label={text} title={text}>{icons.info}</span>;
}

/** Widths and the today mark depend on each program's months, so they are the only computed styles here. */
const share = (f: number) => ({ width: `${f * 100}%` });
const at = (pct: number) => ({ left: `${pct}%` });
const PHASE_GROUP: Record<Stage, "crush" | "aging" | "bottling" | "warehouse"> = {
  sourcing: "crush", farming: "crush", receiving: "crush", crush_press: "crush", fermentation: "crush", aging: "aging", blending: "bottling", bottling: "bottling", warehouse: "warehouse",
};
/** Harvest to release: crush, aging, bottling and warehouse in proportion to their months, with today marked. */
function Timeline({ t }: { t: ProgramRow["timeline"] }) {
  if (!t.phases.length) return <span className="muted">—</span>;
  const start = Math.min(...t.phases.map(p => p.start));
  const end = Math.max(...t.phases.map(p => p.end)) + 1;
  const total = end - start;
  const groups: { id: string; months: number }[] = [];
  for (const p of t.phases) {
    const id = PHASE_GROUP[p.stage];
    const last = groups.at(-1);
    const covered = groups.reduce((a, g) => a + g.months, 0);
    const months = Math.max(0, p.end + 1 - (start + covered));
    if (!months) continue;
    if (last && last.id === id) last.months += months; else groups.push({ id, months });
  }
  const now = t.nowMonth < start ? null : Math.min(100, ((t.nowMonth - start + 0.5) / total) * 100);
  return (
    <div className="mtl" role="img" aria-label={`${t.label}, ${total} months from harvest to release`}>
      <div className="mtl__bar">
        {groups.map((g, i) => <i key={i} className={`mtl__seg mtl__seg--${g.id}`} style={share(g.months / total)} />)}
        {now !== null && <b className="mtl__now" style={at(now)} />}
      </div>
      <span className="small">{t.label}</span>
    </div>
  );
}

/** Quiet inside tolerance and under the materiality floor; amber outside both, red at twice tolerance (H2, H8). */
function VarianceCell({ v }: { v: Variance }) {
  if (v.state === "none") return <StatusChip tone="neutral" quiet>No actuals yet</StatusChip>;
  const pct = v.pct === null ? "—" : `${v.pct > 0 ? "+" : ""}${num(v.pct)}%`;
  const amount = `${v.deltaCents > 0 ? "+" : v.deltaCents < 0 ? "-" : ""}${usd(Math.abs(v.deltaCents), 0)}`;
  const spoken = v.pct === null ? `${usd(Math.abs(v.deltaCents), 0)} ${v.direction === "under" ? "under" : "over"} plan` : `${num(Math.abs(v.pct))}% ${v.direction === "under" ? "under" : "over"} plan`;
  const tone = v.state === "bad" ? "destructive" : v.state === "warn" ? "warning" : "success";
  return (
    <div className="progvar">
      <StatusChip tone={tone} quiet={v.state === "ok"}>
        {v.state === "ok" ? icons.check : icons.warning}
        <span aria-hidden="true">{pct} · {amount}</span><span className="sr">{v.direction ? spoken : "On plan"}</span>
      </StatusChip>
      {v.stagesOutside > 0 && <span className="progvar__n small">{v.stagesOutside} {v.stagesOutside === 1 ? "stage" : "stages"} outside</span>}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="tablewrap" aria-busy="true" aria-label="Loading programs">
      <table className="t">
        <thead><tr>{COLUMNS.map(c => <th key={c.key} scope="col" className={c.right ? "r" : undefined}>{c.label}</th>)}</tr></thead>
        <tbody>{[0, 1, 2, 3].map(i => <tr key={i} className="progskelrow">{COLUMNS.map(c => <td key={c.key}><i /></td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

/* ---------------------------------------------------------------- cancel */

function CancelProgram({ program, onDone }: { program: ProgramRow; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const headingId = useId();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { field.current?.focus(); }, []);
  const cancel = useMutation({
    mutationFn: () => api(`/programs/${program.id}/cancel`, { body: { reason } }),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["programs"] }); toast(`Cancelled ${program.name}.`); onDone(); },
    onError: e => { setError(message(e)); field.current?.focus(); },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); setError(null); cancel.mutate(); };
  return (
    <form className="card progcancel" aria-labelledby={headingId} onSubmit={submit} onKeyDown={e => { if (e.key === "Escape") onDone(); }}>
      <h2 className="card__t" id={headingId}>Cancel {program.name}?</h2>
      <p className="muted">It has no lots or posted cost. It stays readable under Cancelled.</p>
      <Field label="Why are you cancelling it?" error={error}>
        <textarea ref={field} rows={2} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} />
      </Field>
      <div className="actions">
        <button type="submit" className="btn btn--destructive" disabled={cancel.isPending}>Cancel program</button>
        <button type="button" className="btn" onClick={onDone}>Keep it</button>
      </div>
    </form>
  );
}

/* ---------------------------------------------------------------- new program */

type FieldKey = "template" | "name" | "vintage" | "company" | "cases" | "tons" | "acres" | "tonsPerAcre";
/** Which field an error message belongs to, so it can be marked invalid and focused (B, New program error). */
function fieldOf(code: string, text: string): FieldKey | null {
  if (code === "name_taken" || text.startsWith("Give the program a name")) return "name";
  if (text.startsWith("Enter a vintage")) return "vintage";
  if (text.startsWith("Target cases")) return "cases";
  if (text.startsWith("Acres")) return "acres";
  if (text.startsWith("Tons per acre")) return "tonsPerAcre";
  if (text.startsWith("Choose the company")) return "company";
  if (code === "template_archived" || text.startsWith("That template")) return "template";
  return null;
}

function NewProgramForm({ initialTemplate, onClose }: { initialTemplate: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [, navigate] = useLocation();
  const headingId = useId();
  const year = new Date().getFullYear();
  const [templateId, setTemplateId] = useState(initialTemplate ?? "");
  const [name, setName] = useState("");
  const [vintage, setVintage] = useState(String(year));
  const [companyId, setCompanyId] = useState<string>("");
  const [cases, setCases] = useState("");
  const [tons, setTons] = useState("");
  const [acres, setAcres] = useState("");
  const [tonsPerAcre, setTonsPerAcre] = useState("");
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [error, setError] = useState<{ text: string; field: FieldKey | null } | null>(null);
  const idemKey = useRef(crypto.randomUUID());
  const refs = useRef<Partial<Record<FieldKey, HTMLInputElement | HTMLSelectElement | null>>>({});

  const formQ = new URLSearchParams({ ...(templateId ? { templateId } : {}), ...(companyId ? { companyId } : {}) }).toString();
  const form = useQuery({ queryKey: ["programs-new-form", templateId, companyId], queryFn: () => api<NewForm>(`/programs/new-form?${formQ}`), placeholderData: p => p, retry: false });
  const data = form.data;
  useEffect(() => { refs.current.template?.focus(); }, []);
  useEffect(() => {
    if (!data) return;
    if (!templateId && data.templates[0]) setTemplateId(data.templates[0].id);
    if (!companyId && data.defaultCompanyId) setCompanyId(data.defaultCompanyId);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (data?.estate && data.tonsPerAcre != null && !tonsPerAcre) setTonsPerAcre(String(data.tonsPerAcre)); }, [data?.estate, data?.tonsPerAcre]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // a template's base cases and tons are a starting point the person can change
    const t = data?.templates.find(x => x.id === templateId);
    if (t && !cases && t.baseCases) setCases(String(t.baseCases));
    if (t && !tons && t.baseTons != null && !data?.estate) setTons(String(t.baseTons));
  }, [templateId, data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (form.error) setError({ text: message(form.error), field: "template" }); }, [form.error]);

  const estate = !!data?.estate;
  const unmatched = data?.unmatchedPools ?? [];
  const derivedTons = Number(acres) > 0 && Number(tonsPerAcre) > 0 ? Number(acres) * Number(tonsPerAcre) : null;
  const companyName = data?.companies.find(c => c.id === companyId)?.name ?? "this company";
  const noTemplates = !!data && data.templates.length === 0;
  const ready = !noTemplates && unmatched.every(u => picks[u.name]);

  const create = useMutation({
    mutationFn: () => api<{ program: { id: string; name: string; stage: string | null; templateName: string } }>("/programs", {
      body: {
        templateId, name, vintage: Number(vintage), companyId: companyId || null, targetCases: cases === "" ? null : Number(cases),
        ...(estate ? { acres: acres === "" ? null : Number(acres), tonsPerAcre: tonsPerAcre === "" ? null : Number(tonsPerAcre) } : { tons: tons === "" ? null : Number(tons) }),
        poolMatches: unmatched.map(u => ({ templatePoolName: u.name, costPoolId: picks[u.name] })).filter(p => p.costPoolId),
      },
      headers: { "Idempotency-Key": idemKey.current },
    }),
    onSuccess: async ({ program }) => {
      await qc.invalidateQueries({ queryKey: ["programs"] });
      toast(`Created ${program.name} from "${program.templateName}".`);
      navigate(`/builder?program=${program.id}&stage=${program.stage ?? "sourcing"}`);
    },
    onError: e => {
      // keep the key: if the create landed but its response was lost, the retry replays it instead of
      // hitting name_taken; a refused create inserted nothing, so the key is still unused
      const text = message(e);
      const f = fieldOf(e instanceof ApiError ? e.code : "", text);
      setError({ text, field: f });
      if (e instanceof ApiError && e.code === "pool_unmatched") void form.refetch();
      requestAnimationFrame(() => (f ? refs.current[f]?.focus() : undefined));
    },
  });
  const submit = (e: FormEvent) => { e.preventDefault(); setError(null); create.mutate(); };
  const bad = (f: FieldKey) => (error?.field === f ? error.text : null);
  const ref = (f: FieldKey) => (el: HTMLInputElement | HTMLSelectElement | null) => { refs.current[f] = el; };

  return (
    <form className="card prognew" aria-labelledby={headingId} onSubmit={submit} noValidate onKeyDown={e => { if (e.key === "Escape") onClose(); }}>
      <div className="card__h">
        <div>
          <h2 className="card__t" id={headingId}>New program</h2>
          <p className="card__d">Starts from a template's route, yields, hours and quantities. Each activity is sized from this program's volume, and its hours come from the team's estimate for that size. You can change everything afterwards.</p>
        </div>
      </div>
      {noTemplates ? (
        <InlineAlert variant="info" title="There are no templates to start from.">
          Forward Rise support can load the starter templates for white, rosé, red and sparkling. Or save a program as a template. <Link href="/templates">Go to Templates</Link>
        </InlineAlert>
      ) : null}
      <div className="prognew__grid">
        <Field label="Start from" error={bad("template")}>
          <select ref={ref("template")} value={templateId} onChange={e => { setTemplateId(e.target.value); setPicks({}); setTonsPerAcre(""); setError(null); }} disabled={noTemplates}>
            {data?.templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Field>
        <Field label="Program name" error={bad("name")}>
          <input ref={ref("name")} value={name} maxLength={80} placeholder="2026 Petit Verdot" autoComplete="off" onChange={e => setName(e.target.value)} />
        </Field>
        <Field label="Vintage" error={bad("vintage")}>
          <input ref={ref("vintage")} inputMode="numeric" value={vintage} onChange={e => setVintage(e.target.value)} />
        </Field>
        {data?.showCompany && (
          <Field label="Producing company" error={bad("company")} hint={<Tip term="Producing company" />}>
            <select ref={ref("company")} value={companyId} onChange={e => { setCompanyId(e.target.value); setPicks({}); }}>
              {data.companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        )}
        <Field label="Target cases" error={bad("cases")}>
          <input ref={ref("cases")} inputMode="numeric" value={cases} onChange={e => setCases(e.target.value)} />
        </Field>
        {estate ? (
          <>
            <Field label="Acres" error={bad("acres")}>
              <input ref={ref("acres")} inputMode="decimal" value={acres} onChange={e => setAcres(e.target.value)} />
            </Field>
            <Field label="Tons per acre" error={bad("tonsPerAcre")} hint={derivedTons !== null ? `About ${num(derivedTons, 2)} tons.` : undefined}>
              <input ref={ref("tonsPerAcre")} inputMode="decimal" value={tonsPerAcre} onChange={e => setTonsPerAcre(e.target.value)} />
            </Field>
          </>
        ) : (
          <Field label="Tons" error={bad("tons")}>
            <input ref={ref("tons")} inputMode="decimal" value={tons} onChange={e => setTons(e.target.value)} />
          </Field>
        )}
      </div>
      {unmatched.length > 0 && (
        <fieldset className="prognew__pools">
          <legend>Some pools in this template aren't set up in {companyName}. Pick the pool to use for each.</legend>
          <div className="prognew__grid">
            {unmatched.map(u => (
              <Field key={u.name} label={u.name}>
                <select value={picks[u.name] ?? ""} onChange={e => setPicks(p => ({ ...p, [u.name]: e.target.value }))}>
                  <option value="">Pick a pool</option>
                  {u.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
            ))}
          </div>
        </fieldset>
      )}
      {error && !error.field && <InlineAlert variant="error" title={error.text} />}
      {error?.field && <p className="sr" role="alert">{error.text}</p>}
      <div className="actions">
        <button type="submit" className="btn btn--primary" disabled={!ready || create.isPending || !templateId}>Create and open</button>
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
